import { ALLOW_ALL_LOCALES } from 'acscp/settings/appSettings';
import de from './locales/de.locale.json';
import enGB from './locales/en-GB.locale.json';
import extra from './locales/extraMessages.data.json';
import enUS from './locales/en-US.locale.json';
import es from './locales/es.locale.json';
import esMX from './locales/es-MX.locale.json';
import fr from './locales/fr.locale.json';
import it from './locales/it.locale.json';
import ptBR from './locales/pt-BR.locale.json';
import ru from './locales/ru.locale.json';
import th from './locales/th.locale.json';
import zh from './locales/zh.locale.json';
import { registerLocale } from 'react-datepicker';
import { Locale } from 'date-fns';
import deDates from 'date-fns/locale/de';
import enGBDates from 'date-fns/locale/en-GB';
import enUSDates from 'date-fns/locale/en-US';
import esDates from 'date-fns/locale/es';
import frDates from 'date-fns/locale/fr';
import itDates from 'date-fns/locale/it';
import ptBRDates from 'date-fns/locale/pt-BR';
import ruDates from 'date-fns/locale/ru';
import thDates from 'date-fns/locale/th';
import zhDates from 'date-fns/locale/zh-CN';
import countries from './countries/countries.json';
import log from 'loglevel';

const fullEnglishTranslations = {...extra, ...enGB};

const supportedLocales = {
    'en-GB': {...fullEnglishTranslations},
    'en-US': {...fullEnglishTranslations, ...enUS},
    'zh': {...fullEnglishTranslations, ...zh},
    'fr': {...fullEnglishTranslations, ...fr},
    'de': {...fullEnglishTranslations, ...de},
    'pt-BR': {...fullEnglishTranslations, ...ptBR},
    'ru': {...fullEnglishTranslations, ...ru},
    'es': {...fullEnglishTranslations, ...es},
    'es-MX': {...fullEnglishTranslations, ...esMX},
    'it': {...fullEnglishTranslations, ...it},
    'th': {...fullEnglishTranslations, ...th},
};

const fallbackLocales = {
    en: {...fullEnglishTranslations},
    pt: {...fullEnglishTranslations, ...ptBR},
};

export const locales = {
    ...supportedLocales,
    ...fallbackLocales,
};

export type LocaleCode = keyof typeof locales;

/**
 * Register locales for datepicker
 */
getAllowedLocaleCodes().forEach(code => registerLocale(code, getDateLocaleData(code)));

/**
 * The list of LocaleCodes that can be _used_ - languages that we can display the content in
 * (e.g. for automatic language detection, for negotiation between the preferred by the server language).
 *
 * Note that while technically the code-behind could support more languages (see all locales above),
 * we don't have proper translations / don't expect users from other countries / not ready to support them / etc.
 * So only a subset of languages is used.
 */
export function getAllowedLocaleCodes(): LocaleCode[] {
    const alwaysEnabledLocales: LocaleCode[] = ['en-GB', 'en-US', 'en'];
    return ALLOW_ALL_LOCALES ? Object.keys(locales) as LocaleCode[] : alwaysEnabledLocales;
}

/**
 * The list of LocaleCodes that we claim to support when displaying information to user
 * (e.g. when showing the language selection menu)
 *
 * This is likely to be slightly different from all allowed locales.
 * We allow short locale codes ('en') to be able to support unusual locales ('en-BZ'),
 * but we don't want to display them as a separate language (only the actual language that we use as fallback should be shown).
 */
export function getDisplayedLocaleCodes(): LocaleCode[] {
    return getAllowedLocaleCodes().filter(l => Object.keys(fallbackLocales).indexOf(l) === -1);
}

function getDateLocaleData(localeCode: LocaleCode): Locale {
    switch (localeCode) {
        case 'en-GB': return enGBDates;
        case 'en-US': return enUSDates;
        case 'en': return enGBDates;
        case 'zh': return zhDates;
        case 'fr': return frDates;
        case 'de': return deDates;
        case 'pt-BR': return ptBRDates;
        case 'pt': return ptBRDates;
        case 'ru': return ruDates;
        case 'es': return esDates;
        case 'es-MX': return esDates;
        case 'it': return itDates;
        case 'th': return thDates;
    }
}

/**
 * Get a "date-fns" format string for displaying dates.
 * This matches the short version of "Long localized date" i.e. 'P' formatting pattern.
 * For example, this will return MM/DD/YYYY for US locale.
 */
export function getDateFormatStringForLocale(localeCode: LocaleCode): string {
    const localeData = getDateLocaleData(localeCode);
    return (localeData.formatLong as any).date({width: 'short'});
}

/** Finds the closest matching locale we have. So if someone requests fr-CA we can
 * still give them fr.
 */
export function getValidLocale(preferredLocale: string | null): LocaleCode {
    return getFirstValidLocaleFromList([preferredLocale]);
}

export function getFirstValidLocaleFromList(languageCodes: ReadonlyArray<string | null>): LocaleCode {
    const validCodes = languageCodes.filter(code => !!code) as string[];

    if (validCodes.length > 0) {
        const normalizedCodes = validCodes.reduce(
            (allCodes, validCode) => allCodes.concat(validCode, validCode.split('-')[0]),
            [] as string[]
        )
        .map(c => c.toLowerCase());

        const allowedLocaleCodes = getAllowedLocaleCodes().map(code => ({
            code,
            normalizedCode: code.toLowerCase()
        }));

        for (const preferred of normalizedCodes) {
            const matching = allowedLocaleCodes.filter(allowed => allowed.normalizedCode === preferred);

            if (matching.length > 0) {
                return matching[0].code;
            }
        }
    }

    log.warn('Unsupported locale requested: ', languageCodes);
    return 'en-GB';
}

export function getCountryNameTranslation(countryCode: string | null, localeCode: LocaleCode): string | undefined {
    if (!countryCode) {
        return undefined;
    }

    return countries[localeCode]
        ? countries[localeCode][countryCode]
        : undefined;
}
